import React from 'react';
import { Controller, type Control } from 'react-hook-form';
import { Checkbox, Label, Radio } from '@shared/components';
import { BENEATH_TITLE, TITLED_COLUMN } from './fieldLayout';
import type {
    SmriqPlanFormInput,
    SmriqPlanFormOutput,
} from '../../../../schemas/smriqJourney.schema';

type MotChoice = SmriqPlanFormInput['include_mot_tests'];

interface MotFieldProps {
    control: Control<SmriqPlanFormInput, unknown, SmriqPlanFormOutput>;
    allowMotSelection: boolean;
    motChoiceRequired: boolean;
    motResolved: MotChoice;
    fieldsDisabled: boolean;
    onFieldsChange: () => void;
}

const MOT_CHOICES: ReadonlyArray<{ value: Exclude<MotChoice, ''>; label: string }> = [
    { value: 'yes', label: 'Yes' },
    { value: 'no', label: 'No' },
];

export const MotField: React.FC<MotFieldProps> = ({
    control,
    allowMotSelection,
    motChoiceRequired,
    motResolved,
    fieldsDisabled,
    onFieldsChange,
}) =>
    allowMotSelection ? (
        <Controller
            name="include_mot_tests"
            control={control}
            render={({ field, fieldState: { error: fe } }) =>
                motChoiceRequired ? (
                    <div className={TITLED_COLUMN}>
                        <Label id="smriq_include_mot_tests_label" required>
                            MOT tests
                        </Label>
                        <div
                            role="radiogroup"
                            aria-labelledby="smriq_include_mot_tests_label"
                            aria-required="true"
                            aria-invalid={!!fe}
                            aria-describedby={fe ? 'smriq_include_mot_tests-hint' : undefined}
                            className="flex flex-1 items-center gap-6"
                        >
                            {MOT_CHOICES.map(({ value, label }) => (
                                <Radio
                                    key={value}
                                    id={`smriq_include_mot_tests_${value}`}
                                    name="smriq_include_mot_tests"
                                    value={value}
                                    label={label}
                                    checked={field.value === value}
                                    disabled={fieldsDisabled}
                                    onChange={(next) => {
                                        field.onChange(next);
                                        onFieldsChange();
                                    }}
                                />
                            ))}
                        </div>
                        {fe && (
                            <p
                                id="smriq_include_mot_tests-hint"
                                className="mt-1.5 text-xs text-mp-error"
                            >
                                {fe.message}
                            </p>
                        )}
                    </div>
                ) : (
                    <div className={TITLED_COLUMN}>
                        <Label htmlFor="smriq_include_mot_tests">&nbsp;</Label>
                        <div className={BENEATH_TITLE}>
                            <Checkbox
                                id="smriq_include_mot_tests"
                                label="Include MOT tests"
                                ariaLabel="Include MOT tests"
                                checked={field.value === 'yes'}
                                disabled={fieldsDisabled}
                                onChange={(checked) => {
                                    field.onChange(checked ? 'yes' : 'no');
                                    onFieldsChange();
                                }}
                            />
                        </div>
                    </div>
                )
            }
        />
    ) : (
        <div className={TITLED_COLUMN}>
            <Label>MOT tests</Label>
            <div className={BENEATH_TITLE}>
                <p className="text-sm text-mp-foreground">
                    {motResolved === 'yes' ? 'MOTs included' : 'MOTs not included'}
                </p>
            </div>
        </div>
    );
